// src/Dashboard/pages/teacher/Events/hooks/useEventsDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createEvent,
  deleteEvent,
  getAllEvents,
  toggleEventActive,
  toggleEventRegistration,
  updateEvent,
} from "../../../../../services/eventServices";

import { getSettings } from "../../../../../services/settingsServices";
import { uploadEventPhoto } from "../../../../../services/eventPhotoServices";

import {
  buildEventPayload,
  calculateEventsStats,
  CATEGORY_OPTIONS,
  exportEvents,
  filterEvents,
  getTypeOptions,
  LEVEL_OPTIONS,
  paginateEvents,
  sortEvents,
} from "../utils/eventsDashboardUtils";

const PAGE_SIZE = 10;

const defaultFilters = {
  query: "",
  dateStatus: "upcoming",
  type: "All",
  level: "All",
  category: "All",
  visibility: "All",
  registration: "All",
  sort: "date_asc",
};

export function useEventsDashboard(user) {
  const [events, setEvents] = useState([]);
  const [shortnameClub, setShortnameClub] = useState("");

  const [filters, setFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingIds, setTogglingIds] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [currentEvent, setCurrentEvent] = useState(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const [detailsEvent, setDetailsEvent] = useState(null);

  const [groupMessageOpen, setGroupMessageOpen] = useState(false);
  const [groupMessageEvent, setGroupMessageEvent] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");

  const typeOptions = useMemo(() => {
    return getTypeOptions(shortnameClub);
  }, [shortnameClub]);

  const levelOptions = LEVEL_OPTIONS;
  const categoryOptions = CATEGORY_OPTIONS;

  const fetchSettings = useCallback(async () => {
    try {
      const data = await getSettings();
      const settings = Array.isArray(data) ? data[0] : data;

      setShortnameClub(settings?.shortname_club || "");
    } catch (error) {
      console.error("Erreur paramètres événements :", error);
      setShortnameClub("");
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement événements :", error);
      toast.error("Impossible de charger les événements.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchEvents();
  }, [fetchSettings, fetchEvents]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const filteredEvents = useMemo(() => {
    return sortEvents(filterEvents(events, filters), filters.sort);
  }, [events, filters]);

  const paginatedEvents = useMemo(() => {
    return paginateEvents(filteredEvents, page, PAGE_SIZE);
  }, [filteredEvents, page]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  }, [filteredEvents.length]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const stats = useMemo(() => {
    return calculateEventsStats(events, filteredEvents);
  }, [events, filteredEvents]);

  const openCreateDrawer = () => {
    setDrawerMode("create");
    setCurrentEvent(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (event) => {
    setDrawerMode("edit");
    setCurrentEvent(event);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving || uploadingImage) return;

    setDrawerOpen(false);
    setCurrentEvent(null);
  };

  const openUploadModal = () => {
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
  };

  const openDetailsModal = (event) => {
    setDetailsEvent(event);
  };

  const closeDetailsModal = () => {
    setDetailsEvent(null);
  };

  const openGroupMessageModal = (event) => {
    setGroupMessageEvent(event);
    setGroupMessageOpen(true);
  };

  const closeGroupMessageModal = () => {
    setGroupMessageOpen(false);
    setGroupMessageEvent(null);
  };

  const openPreview = (src) => {
    setPreviewSrc(src || "/img/no-image.jpg");
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewSrc("");
  };

  const handleCreateEvent = async (formData) => {
    try {
      setSaving(true);

      const payload = buildEventPayload(formData);
      await createEvent(payload);

      toast.success("Événement créé avec succès.");
      setDrawerOpen(false);

      await fetchEvents();
    } catch (error) {
      console.error("Erreur création événement :", error);
      toast.error("Erreur lors de la création de l’événement.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvent = async (formData) => {
    if (!currentEvent?.id) {
      toast.error("Événement introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildEventPayload(formData);
      await updateEvent(currentEvent.id, payload);

      toast.success("Événement mis à jour.");
      setDrawerOpen(false);
      setCurrentEvent(null);

      await fetchEvents();
    } catch (error) {
      console.error("Erreur mise à jour événement :", error);
      toast.error("Erreur lors de la mise à jour de l’événement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventOrId) => {
    const id = typeof eventOrId === "object" ? eventOrId?.id : eventOrId;

    if (!id) {
      toast.error("Événement introuvable.");
      return;
    }

    if (!window.confirm("Supprimer cet événement ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteEvent(id);

      setEvents((prev) => prev.filter((event) => event.id !== id));
      toast.success("Événement supprimé.");
    } catch (error) {
      console.error("Erreur suppression événement :", error);
      toast.error("Suppression impossible.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (event) => {
    if (!event?.id) return;

    const previous = !!event.is_active;
    const optimistic = !previous;

    setEvents((prev) =>
      prev.map((item) =>
        item.id === event.id ? { ...item, is_active: optimistic } : item
      )
    );

    try {
      setTogglingIds((prev) => ({ ...prev, [`active-${event.id}`]: true }));

      const updated = await toggleEventActive(event.id);

      setEvents((prev) =>
        prev.map((item) =>
          item.id === event.id
            ? {
                ...item,
                is_active: updated?.is_active ?? optimistic,
              }
            : item
        )
      );
    } catch (error) {
      console.error("Erreur toggle affichage événement :", error);

      setEvents((prev) =>
        prev.map((item) =>
          item.id === event.id ? { ...item, is_active: previous } : item
        )
      );

      toast.error("Erreur lors du changement d’affichage.");
    } finally {
      setTogglingIds((prev) => ({ ...prev, [`active-${event.id}`]: false }));
    }
  };

  const handleToggleRegistration = async (event) => {
    if (!event?.id) return;

    const previous = Number(event.can_register ?? 1);
    const optimistic = previous ? 0 : 1;

    setEvents((prev) =>
      prev.map((item) =>
        item.id === event.id ? { ...item, can_register: optimistic } : item
      )
    );

    try {
      setTogglingIds((prev) => ({
        ...prev,
        [`registration-${event.id}`]: true,
      }));

      const response = await toggleEventRegistration(event.id);
      const next = Number(
        response?.can_register ?? response?.event?.can_register ?? optimistic
      );

      setEvents((prev) =>
        prev.map((item) =>
          item.id === event.id ? { ...item, can_register: next } : item
        )
      );

      toast.success(next ? "Inscriptions ouvertes." : "Inscriptions closes.");
    } catch (error) {
      console.error("Erreur toggle inscriptions événement :", error);

      setEvents((prev) =>
        prev.map((item) =>
          item.id === event.id ? { ...item, can_register: previous } : item
        )
      );

      toast.error("Erreur lors du changement du statut d’inscription.");
    } finally {
      setTogglingIds((prev) => ({
        ...prev,
        [`registration-${event.id}`]: false,
      }));
    }
  };

  const handleUploadEventImage = async ({ eventId, file, format, onUploadProgress }) => {
    if (!eventId) {
      toast.error("Enregistrez d’abord l’événement avant d’ajouter une image.");
      return null;
    }

    if (!file) {
      toast.warn("Sélectionnez une image.");
      return null;
    }

    const isValidType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

    if (!isValidType) {
      toast.error("Format non supporté. Utilisez PNG, JPG ou WEBP.");
      return null;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux. Maximum 10 Mo.");
      return null;
    }

    try {
      setUploadingImage(true);

      const updated = await uploadEventPhoto(eventId, file, {
        format,
        onUploadProgress,
      });

      const photoUrl =
        updated?.photo_url ||
        updated?.event?.photo_url ||
        updated?.data?.photo_url ||
        "";

      if (photoUrl) {
        setEvents((prev) =>
          prev.map((event) =>
            event.id === eventId ? { ...event, photo_url: photoUrl } : event
          )
        );

        setCurrentEvent((prev) =>
          prev?.id === eventId ? { ...prev, photo_url: photoUrl } : prev
        );
      }

      toast.success("Image téléversée et optimisée.");

      return photoUrl;
    } catch (error) {
      console.error("Erreur upload image événement :", error);
      toast.error("Erreur lors du téléversement de l’image.");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleExportFiltered = useCallback(async () => {
    try {
      await exportEvents(filteredEvents);
      toast.success("Export généré.");
    } catch (error) {
      console.error("Erreur export événements :", error);
      toast.error("Échec de l’export des événements filtrés.");
    }
  }, [filteredEvents]);

  const refreshEvents = useCallback(async () => {
    await fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    filteredEvents,
    paginatedEvents,
    stats,

    filters,
    setFilters,
    page,
    setPage,
    totalPages,
    pageSize: PAGE_SIZE,

    shortnameClub,
    typeOptions,
    levelOptions,
    categoryOptions,

    loading,
    saving,
    deletingId,
    togglingIds,
    uploadingImage,

    drawerOpen,
    drawerMode,
    currentEvent,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    uploadModalOpen,
    openUploadModal,
    closeUploadModal,

    detailsEvent,
    openDetailsModal,
    closeDetailsModal,

    groupMessageOpen,
    groupMessageEvent,
    openGroupMessageModal,
    closeGroupMessageModal,

    previewOpen,
    previewSrc,
    openPreview,
    closePreview,

    handleCreateEvent,
    handleUpdateEvent,
    handleDeleteEvent,
    handleToggleActive,
    handleToggleRegistration,
    handleUploadEventImage,
    handleExportFiltered,
    refreshEvents,
  };
}